import { useCallback, useEffect, useState } from 'react';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { fetchLltSearch } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type LltSearchRow, type ReagentDateFilter } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → LLT Search (nav `rgp-lltsearch`).
// Port of legacy ALPACA `ReagentPreparation_ListView_LLTSearch`; spec §16.1.
//
// Read-only BY DESIGN, in legacy as well: the view sets AllowNew / AllowDelete
// false and hides the Edit action.  The screen exists to FIND an LLT and then
// print its report or label.  Those two toolbar actions need the report
// endpoint (the PDF renderer itself already exists — `LltReagentCoaShaper`) and
// arrive in a later wave; until then the grid's own search, per-column filters,
// sort and export are the working surface.
//
// Columns (legacy Model.xafml order): LLT · Division · PrepType · PrepName ·
// Department · ExpirationDate · PreparedBy · DatePrepared, sorted LLT DESC.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<LltSearchRow>[] = [
  { field: 'llt',            header: 'LLT',            width: 120 },
  { field: 'division',       header: 'Division',       width: 200 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'prepName',       header: 'PrepName',       width: 220 },
  { field: 'department',     header: 'Department',     width: 120 },
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 150, type: 'datetime' },
];

export default function LltSearchPage() {
  const notify = useNotify();
  const [rows, setRows]       = useState<LltSearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchLltSearch(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load LLT search.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="bl-page">
      <FormTitle pageId="rgp-lltsearch" loadMs={loadMs} loading={loading}
                 auditFormName="Reagent Prep Log" />
      <AppToolbar
        left={
          <>
            <DateRangeSelect
              variant="dropdown" label="Date Filter" ranges={REAGENT_RANGES}
              value={dateFilter ? rangeDays(dateFilter) : null}
              onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
            />
            {/* Reports ▾ — lab-assigned to ReagentPreparation_ListView_LLTSearch. */}
            <FormReportsMenu
              reportsUrl="/api/v1/reagent-preps/reports?view=llt-search"
              pdfUrl="/api/v1/reagent-preps/report-pdf"
              keysParam="llts"
              selectedKeys={rows.filter((r) => selected.has(r.oid) && r.llt).map((r) => r.llt!)}
            />
          </>
        }
        onRefresh={() => load(dateFilter ?? undefined)}
        refreshing={loading}
        exportData={{ filename: 'llt-search', ...gridExportMatrix(rows, COLUMNS) }}
      />
      <AppDataGrid<LltSearchRow>
        rows={rows}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        loading={loading}
        emptyText="No lab lots found for this date range."
      />
    </div>
  );
}
